import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InventoryIcon from '@mui/icons-material/Inventory';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import fileManagerService from '../../services/file-manager-service';

async function resolveSignedUrls(urls) {
  return Promise.all(
    urls.map((url) =>
      url?.startsWith('https://')
        ? fileManagerService.getSignedUrl(url)
        : Promise.resolve(url)
    )
  );
}

function ItemField({ label, value }) {
  if (!value) return null;
  return (
    <Typography variant="body2" sx={{ mb: 0.5 }}>
      <strong>{label}:</strong> {value}
    </Typography>
  );
}

function ItemPhotoSection({ title, photos, onImageClick }) {
  const [signedPhotos, setSignedPhotos] = useState([]);

  useEffect(() => {
    if (photos?.length > 0) {
      resolveSignedUrls(photos).then(setSignedPhotos);
    } else {
      setSignedPhotos([]);
    }
  }, [photos]);

  if (signedPhotos.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        {title} ({signedPhotos.length})
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {signedPhotos.map((url, i) => (
          <Box
            key={i}
            onClick={() => onImageClick(url)}
            sx={{
              width: 120,
              height: 120,
              borderRadius: 1,
              overflow: 'hidden',
              cursor: 'pointer',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.04)' }
            }}
          >
            <img
              src={url}
              alt={`${title} ${i + 1}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ItemCard({ item, index, onImageClick }) {
  const mpn = item.mpn ?? item.msku;
  const msn = item.msn ?? item.sn;
  const imei1 = item.imei1 ?? item.imei;
  const imei2 = item.imei2;

  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderRadius: 2, backgroundColor: 'background.default' }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Item {index + 1}
        {item.upc ? ` — UPC: ${item.upc}` : ''}
      </Typography>
      <ItemField label="UPC" value={item.upc} />
      <ItemField label="MPN" value={mpn} />
      <ItemField label="MSN" value={msn} />
      <ItemField label="IMEI 1" value={imei1} />
      <ItemField label="IMEI 2" value={imei2} />
      <ItemField label="Transparency Code" value={item.transparency_code} />
      <ItemPhotoSection
        title="Front View Photos"
        photos={item.front_view_photos}
        onImageClick={onImageClick}
      />
      <ItemPhotoSection
        title="Label / Barcode Photos"
        photos={item.label_barcode_photos}
        onImageClick={onImageClick}
      />
    </Card>
  );
}

export default function PackageDetailsItemInfoTab({ items = [], onImageClick }) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          <InventoryIcon sx={{ mr: 1 }} />
          Item Info
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>
              Collected Items ({items.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No items recorded for this package.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {items.map((item, index) => (
                  <ItemCard
                    key={index}
                    item={item}
                    index={index}
                    onImageClick={onImageClick}
                  />
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
}
